import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { byEndDescending } from "@/lib/format";
import { errorText } from "@/lib/query/client";
import { qk, type QueryFamily } from "@/lib/query/keys";
import { useLiveSync } from "@/lib/query/live-sync";

/** What a board-level action may have changed: the cards, and every list built from them. */
export const BOARD_FAMILIES: readonly QueryFamily[] = [
  "boards",
  "projects",
  "project-usage",
  "users",
  "queue",
  "awaiting",
  "task",
  "response",
  "activity",
  "intake",
  "whatsapp",
];

export const useBoardsQuery = () =>
  useQuery({ queryKey: qk.boards(), queryFn: () => api.listBoards(true) });

/** Users too: an agent created elsewhere is a new assignee and a new @handle. */
export const useUsersQuery = () => useQuery({ queryKey: qk.users(), queryFn: api.users, select: selectUsers });
// Module-level selects: a new function each render would re-run them and hand out new arrays.
const selectUsers = (data: Awaited<ReturnType<typeof api.users>>) => data.users;
const selectProjects = (data: Awaited<ReturnType<typeof api.listProjects>>) => data.projects;

/**
 * Registered directories. Kept current by the live feed like the boards:
 * `createProject` goes through `write()`, so a project added from an MCP session
 * shows up in the pickers without a reload.
 */
export const useProjectsQuery = () =>
  useQuery({ queryKey: qk.projects(), queryFn: api.listProjects, select: selectProjects });

/**
 * Every board, the users and the projects, from the query cache. The change feed
 * that keeps them current is `LiveSyncProvider`; this only reads.
 *
 * Archived boards are fetched with the rest and split out here rather than
 * loaded by the archive page on its own: the split is the only difference
 * between the two lists, and one request keeps the archive count in the sidebar
 * honest without a second poll.
 */
export function useBoards() {
  const boardsQuery = useBoardsQuery();
  const usersQuery = useUsersQuery();
  const projectsQuery = useProjectsQuery();
  const { localChange, remoteChangeAt } = useLiveSync();

  const allBoards = boardsQuery.data?.boards ?? EMPTY;
  const users = usersQuery.data ?? EMPTY_USERS;
  const projects = projectsQuery.data ?? EMPTY_PROJECTS;
  /** A true first load: nothing cached to show. */
  const loading = boardsQuery.isPending;
  const error = boardsQuery.isError
    ? errorText(boardsQuery.error, "Cannot reach the API. Is `bun run dev:server` running on port 4000?")
    : null;

  /** After an action of our own: refetch what it may have touched, batched with its revision event. */
  const refresh = useCallback(async () => localChange(BOARD_FAMILIES), [localChange]);

  /** Live boards, in the order the API returned them (soonest deadline first). */
  const boards = useMemo(() => allBoards.filter((detail) => !detail.board.archived), [allBoards]);
  /** Most recently closed first — an archive is read backwards from now. */
  const archivedBoards = useMemo(
    () => allBoards.filter((detail) => detail.board.archived).sort(byEndDescending),
    [allBoards],
  );

  return { boards, archivedBoards, allBoards, users, projects, loading, error, refresh, remoteChangeAt };
}

const EMPTY: never[] = [];
const EMPTY_USERS: never[] = [];
const EMPTY_PROJECTS: never[] = [];

/** Re-renders on an interval so "2d 4h left" counts down without a refetch. */
export function useNow(intervalMs = 30_000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const handle = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(handle);
  }, [intervalMs]);
  return now;
}
